import { VehicleDetails } from '@shared/models/vehicle-details.model';
import { JobFileServiceModel } from '@core/models/job-file/job-file-service.model';
import { ServiceSequenceTemplate } from '../../wizard/models/service-sequence-template.model';
import { AddContainer } from '../../wizard/models/add-container.model';
import { CollaboratorsInvitationModel } from 'app/modules/wizard/models/collaborators-invitation.model';
import { ContainerTypeAndSize } from '@control-tower/models/jpm/jpm-container-type-and-size.model';
import { LoadKeyDetails } from '@core/models/job-file/job-file.model';
import { BatchSaveServiceItem } from '../services/load-management.service';

export const LOAD_MANAGEMENT_ACTION_PREFIX = '[Load Management]';

export class LoadJobFile {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Load Job File`;
  constructor(public readonly orgId: string, public readonly jobRefId: string) {}
}

export class ResetLoadManagementState {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Reset State`;
}

export class AddNewContainer {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Add New Container`;
  constructor(public readonly vehicleData: VehicleDetails[]) {}
}

export class UpdateService {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Update Service`;
  constructor(
    public readonly orgId: string,
    public readonly jobRefId: string,
    public readonly rootOrgId: string,
    public readonly rootJobRefId: string,
    public readonly containerId: string,
    public readonly service: JobFileServiceModel,
    // Batch save reloads the job file/container once for the whole batch afterwards - dispatching
    // LoadJobFile/GetContainer per row here would just queue redundant HTTP GETs behind each other
    // and starve the browser's per-origin connection pool while the batch is still running.
    public readonly skipReload: boolean = false
  ) {}
}

export class AddService {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Add Service`;
  constructor(
    public readonly orgId: string,
    public readonly jobRefId: string,
    public readonly rootOrgId: string,
    public readonly rootJobRefId: string,
    public readonly containerId: string,
    public readonly index: number,
    public readonly service: JobFileServiceModel,
    public readonly skipReload: boolean = false
  ) {}
}

export class BatchSaveServices {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Batch Save Services`;
  constructor(
    public readonly orgId: string,
    public readonly jobRefId: string,
    public readonly rootOrgId: string,
    public readonly rootJobRefId: string,
    public readonly containerId: string,
    public readonly items: BatchSaveServiceItem[],
    public readonly sessionId?: string,
    // When true, items is deliberately empty - the backend loads them from service_staging_cache
    // by sessionId instead, because the caller already proved (via a hash check) that cache row
    // is byte-identical to what it would otherwise send. See RouteSetupDialogComponent.processBatchSave.
    public readonly useCache: boolean = false
  ) {}
}

export class LoadSavedRoutes {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Load Saved Routes`;
  constructor(public readonly keyword: string = '') {}
}

export class SaveServiceSequenceTemplate {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Save Service Sequence Template`;
  constructor(
    public readonly containerId: string,
    public readonly name: string
  ) {}
}

export class UpdateServiceSequenceTemplate {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Update Service Sequence Template`;
  constructor(
    public readonly containerId: string,
    public readonly templateId: string,
    public readonly name: string
  ) {}
}

export class ApplyServiceSequenceTemplate {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Apply Service Sequence Template`;
  constructor(
    public readonly orgId: string,
    public readonly jobRefId: string,
    public readonly containerId: string,
    public readonly templateId: string
  ) {}
}

export class RemoveServiceSequenceTemplate {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Remove Service Sequence Template`;
  constructor(public readonly templateId: string) {}
}

export class DeleteService {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Delete Service`;
  constructor(
    public readonly orgId: string,
    public readonly jobRefId: string,
    public readonly containerId: string,
    public readonly serviceId: string
  ) {}
}

/**
 * The container no longer exists on the server. Emitted when a container read comes back 404 -
 * normally because deleting the last unloading activity removed the whole load, which is the
 * intended behaviour. Listeners should tear down anything scoped to that container.
 */
export class ContainerRemoved {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Container Removed`;
  constructor(public readonly containerId: string) {}
}

export class UpdateBillingActivated {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Update Billing Activated`;
  constructor(
    public readonly orgId: string,
    public readonly jobRefId: string,
    public readonly containerId: string,
    public readonly service: JobFileServiceModel,
    public readonly billingActivated: boolean
  ) {}
}

export class UpdateLegFreezer {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Update Leg Freezer`;
  constructor(
    public readonly orgId: string,
    public readonly jobRefId: string,
    public readonly containerId: string,
    public readonly legIndex: number,
    public readonly useFreezer: boolean
  ) {}
}

export class RemoveContainer {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Remove Container`;
  constructor(
    public readonly orgId: string,
    public readonly jobRefId: string,
    public readonly containerId: string
  ) {}
}

export class GetContainer {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Get Container`;
  constructor(
    public readonly orgId: string,
    public readonly jobRefId: string,
    public readonly containerId: string
  ) {}
}

export class DuplicateContainer {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Duplicate Container`;
  constructor(
    public readonly orgId: string,
    public readonly jobRefId: string,
    public readonly containerId: string
  ) {}
}

export class GetVehicleCategories {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Get Vehicle Categories`;
  constructor(
    public readonly isStandard: boolean = false,
    public readonly loadType?: string
  ) {}
}

export class GetPartnerOrganizations {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Get Partner Organizations`;
  constructor(
    public readonly searchKey: string = '',
    public readonly pageNum: number = 0,
    public readonly pageSize: number = 1000
  ) {}
}

export class SearchPartnerOrganizations {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Search Partner Organizations`;
  constructor(
    public readonly searchKey: string = '',
    public readonly pageNum: number = 0,
    public readonly pageSize: number = 1000
  ) {}
}

export class GetVehiclesByOrganization {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Get Vehicles By Organization`;
  constructor(public readonly orgId: string) {}
}

export class ResetPartnerOrganizationsAndVehicles {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Reset Partner Organizations And Vehicles`;
}

export class LoadDigitalTwinVehicles {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Load Digital Twin Vehicles`;
  constructor(public payload: {
    orgId: string;
    operationId: string;
    vehicleCategory: string;
    supplierIds: string[];
    searchText: string;
    type: string;
    page?: number;
    size?: number;
    reset?: boolean;
  }) {}
}

export class ResetDigitalTwinVehicles {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Reset Digital Twin Vehicles`;
}

export class LoadDigitalTwinVehiclesByOrg {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Load Digital Twin Vehicles By Org`;
  constructor(public payload: {
    orgId: string;
    operationId?: string;
    vehicleCategory?: string;
    searchText?: string;
    type?: string;
  }) {}
}

export class LoadActivityDefinitions{
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Load Activity Definitions`;
  constructor() {}
}

export class LoadWorkOrderDefinitionsAction {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Load Work Order Definitions`;
  constructor(public readonly workOrderIds: string[]) {}
}

export class LoadWorkflowDefinition {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Load Workflow Definition`;
  constructor(public readonly workflowId: string) {}
}

export class InviteCollaborators {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Invite Collaborators`;
  constructor(public readonly collaboratorsInvitationModel: CollaboratorsInvitationModel) {}
}

export class RemoveCollaborator {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Remove Collaborator`;
  constructor(public readonly collaboratorId: string) {}
}

export class OptimizeContainers {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Optimize Containers`;
  constructor(
    public readonly orgId: string,
    public readonly jobRefId: string,
    public readonly containerIds: string[],
    public readonly useCloudRunSync: boolean = false
  ) {}
}

export class UpdateContainerFcl {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Update Container FCL`;
  constructor(
    public readonly orgId: string,
    public readonly jobRefId: string,
    public readonly containerId: string,
    public readonly payload: {
      containerId: string;
      containerNumber?: string;
      selectionMethod: string;
      typeAndSize: ContainerTypeAndSize;
    }
  ) {}
}

export class UpdateContainerVehicleCategory {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Update Container Vehicle Category`;
  constructor(
    public readonly orgId: string,
    public readonly jobRefId: string,
    public readonly containerId: string,
    public readonly vehicleCategoryId: string
  ) {}
}

export class UpdateContainerCustomsChannel {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Update Container Customs Channel`;
  constructor(
    public readonly orgId: string,
    public readonly jobRefId: string,
    public readonly containerId: string,
    public readonly customsChannel: string
  ) {}
}

export class UpdateLoadKeyDetails {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Update Load Key Details`;
  constructor(
    public readonly orgId: string,
    public readonly jobRefId: string,
    public readonly containerId: string,
    public readonly loadKeyDetails: LoadKeyDetails
  ) {}
}

export class SaveRemark {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Save Remark`;
  constructor(public orgId: string, public jobRefId: string, public containerId: string, public remark: string) {}
}

export class SaveRemarks {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Save Remarks`;
  constructor(public orgId: string, public jobRefId: string, public containerId: string, public remarks: string[]) {}
}

export class DeleteRemark {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Delete Remark`;
  constructor(public orgId: string, public jobRefId: string, public containerId: string, public index: number) {}
}

export class LoadRemarks {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Load Remarks`;
  constructor(public orgId: string, public jobRefId: string, public containerId: string) {}
}

export class SaveWaypoints {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Save Waypoints`;
  constructor(
    public readonly containerId: string,
    public readonly undoStack: any[],
    public readonly redoStack: any[]
  ) {}
}

/** Persist leg waypoints / encoded path after route map drag (load-management only). */
export class UpdateContainerRouteWaypoints {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Update Container Route Waypoints`;
  constructor(
    public readonly orgId: string,
    public readonly jobRefId: string,
    public readonly container: AddContainer
  ) {}
}

export class UpdateContainer {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Update Container`;
  constructor(public readonly container: AddContainer) {}
}

export class FindInitContainersByContainerIds {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Find Init Containers By Container Ids`;
  constructor(
    public readonly orgId: string,
    public readonly jobRefId: string,
    public readonly containerIds: string[]
  ) {}
}

export class SaveOtherReferenceNumber {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Save Other Reference Number`;
  constructor(
    public readonly orgId: string,
    public readonly jobRefId: string,
    public readonly containerId: string,
    public readonly locationId: string,
    public readonly serviceId: string,
    public readonly refNumbers: string[],
    public readonly workOrderId?: string
  ) {}
}

export class DeleteOtherReferenceNumber {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Delete Other Reference Number`;
  constructor(
    public readonly orgId: string,
    public readonly jobRefId: string,
    public readonly containerId: string,
    public readonly locationId: string,
    public readonly serviceId: string,
    public readonly refNumber: string,
    public readonly workOrderId?: string
  ) {}
}

/** PB-5281: labels of the job's operation (skipped when already loaded for it). */
export class LoadOperationLabels {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Load Operation Labels`;
  /** force re-fetches the cached operation, e.g. to pick up a colour changed meanwhile. */
  constructor(public readonly operationId: string | null, public readonly force = false) {}
}

/** PB-5281: which loads of the job are on an invoice. */
export class LoadInvoicedLoadIds {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Load Invoiced Load Ids`;
  constructor(public readonly loadIds: string[]) {}
}

/** PB-5281: replaces the labels of a load. Errors propagate so the drawer can show them. */
export class UpdateContainerLabels {
  static readonly type = `${LOAD_MANAGEMENT_ACTION_PREFIX} Update Container Labels`;
  constructor(
    public readonly orgId: string,
    public readonly jobRefId: string,
    public readonly containerId: string,
    public readonly labelIds: string[]
  ) {}
}
